import { useState, useEffect, useRef, useMemo } from 'react';
import * as React from 'react';
import styled from 'styled-components';
import Column from '@sdk/components/ui/spacing/Column';
import Row from '@sdk/components/ui/spacing/Row';
import FooterSaveActions from '@sdk/components/ui/FooterSaveActions';
import useSlingApi from '@sdk/api/useSlingApi';
import AnchorMenu from '@sdk/components/ui/anchorMenu/AnchorMenu';
import useAuth from '@sdk/authentication/useAuth';
import ToastComponent from '@sdk/common/toast/ToastComponent';
import useToast from '@sdk/common/toast/useToast';
import getErrorMessages from '@sdk/utils/getErrorMessages';
import type { LaborCostSettingsFromApi } from '@sdk/labor/types/apiTypes';
import type { AuthUserType } from '@sdk/authentication/types/AuthUserType';
import type { TimeOffType } from '@sdk/timeOffs/types/TimeOffType';
import type { DayPartExtended, TimeBlockExtended } from '@sdk/dayParts/types';
import { PosIntegrations, IntegrationType } from '@sdk/integrations/types';

import type { $rootScopeType, $stateType } from '../common/types/AngularTypes';

import type {
  GoogleCalendarUtilsType,
  ChangeTab,
  NavigationTabsType,
  NavigationTabType,
  PromisesConfigType,
} from './types';
import NotificationsScreen from './NotificationsScreen';
import PreferencesScreen from './PreferencesScreen';
import SettingsTips from './components/SettingsTips';
import CompanySettingsScreen from './CompanySettingsScreen';
import ScheduleScreen from './ScheduleScreen';
import TimeclockScreen from './TimeclockScreen';
import LaborCostScreen from './LaborCostScreen';
import TimeOffScreen from './TimeOffScreen';
import IntegrationsScreen from './IntegrationsScreen';
import { TimeBlocksScreen } from './TimeBlocksScreen';
import { TimeClockPremiumScreen } from './components/timeclock/TimeClockPremiumScreen';
import { LaborCostPremiumScreen } from './components/laborCost/LaborCostPremiumScreen';
import getAllSectionsConfig from './helpers/getAllSectionsConfig';
import sortTimeOfTypes from './components/timeOff/helpers/sortTimeOfTypes';
import SettingsNavigation from './components/SettingsNavigation';
import laborSettingsFormatter from './components/laborCost/helpers/laborSettingsFormatter';
import { laborSettingsParser } from './components/laborCost/helpers/laborSettingsParser';
import preSaveUserSettingsHandler from './helpers/preSaveUserSettingsHandler';
import preSaveUserOrgSettingsHandler from './helpers/preSaveUserOrgSettingsHandler';
import normalizeOrgSettings from './helpers/normalizeOrgSettings';
import { TabType, PromiseType } from './types';
import { useSettingsErrorsContext } from './SettingsErrorContext';
import {
  getClientSettings,
  initNotificationsClientSettings,
  updateClientSetings,
} from './components/notifications/helpers/handleClientSettings';
import { useDayParts } from './components/timeBlocks/useDayParts';
import { useShiftAlarms } from './components/notifications/shiftAlarms/useShiftAlarms';
import { useUserSettings } from './UserSettingsContext';
import { useUserOrgSettings } from './UserOrgSettingsContext';
import { useSettingsClientFlags } from './SettingsClientFlagsContext';
import { ToastTips } from './components/integrations/ToastTips';

const Wrapper = styled(Column)`
  align-items: center;
`;

const SettingsContainer = styled(Column)`
  width: 1200px;
  padding-bottom: 150px;
  align-items: center;
  flex-shrink: 1;
  position: relative;
`;

const SettingsContent = styled(Row)`
  width: 1200px;
`;

const SettingsGutterColumn = styled(Column)`
  padding-top: 34px;
  flex: 1;
`;

const SectionsContainer = styled(Column)`
  width: 750px;
  padding-top: 20px;
  flex-shrink: 1;
`;

// @TODO: fix TS personaCollection
export type Props = {
  user: AuthUserType;
  personaCollection: any;
  tabs: NavigationTabsType;
  activeTab: NavigationTabType;
  changeTab: ChangeTab;
  laborCostSettings: LaborCostSettingsFromApi;
  saveLaborCostSettings: ({ settings }) => Promise<void>;
  googleCalendarUtils: GoogleCalendarUtilsType;
  // @TODO: type modals
  ConnectCodeModal: any;
  IntegrationParametersModal: any;
  timeOffTypes: TimeOffType[];
  laborCostDataLoaded: boolean;
  isWhitelisted: boolean;
  addEditDayPart: (params: { dayPart?: DayPartExtended }) => Promise<DayPartExtended>;
  addEditTimeBlock: (params: { timeBlock?: TimeBlockExtended }) => Promise<TimeBlockExtended>;
  $rootScope: $rootScopeType;
  $state: $stateType;
  integrations: IntegrationType[];
};

const SettingsScreen: React.FC<React.PropsWithChildren<Props>> = ({
  user,
  personaCollection,
  tabs,
  activeTab,
  changeTab,
  laborCostSettings,
  saveLaborCostSettings,
  googleCalendarUtils,
  ConnectCodeModal,
  IntegrationParametersModal,
  timeOffTypes: rawTimeOffTypes,
  $rootScope,
  laborCostDataLoaded,
  isWhitelisted,
  addEditDayPart,
  addEditTimeBlock,
  $state,
  integrations,
}) => {
  const initialLaborCostSettings = useRef(null);
  const navigationWidth = useRef(null);

  const [initialClientSettingsState, setInitialClientSettingsState] = useState({});
  const [initialTimeOffTypes, setInitialTimeOffTypes] = useState(() =>
    sortTimeOfTypes(rawTimeOffTypes)
  );

  const {
    setInitialUserSettings,
    userSettings,
    isUserSettingsChanged,
    setIsUserSettingsChanged,
    revertUserSettingsChanges,
  } = useUserSettings();
  const {
    userOrgSettings,
    isUserOrgSettingsChanged,
    setIsUserOrgSettingsChanged,
    setInitialUserOrgSettings,
    revertOrgSettingsChanges,
  } = useUserOrgSettings();

  const {
    clientFlags,
    isClientFlagsChanged,
    revertClientFlagsChanges,
    saveAllClientFlags,
    setIsClientFlagsChanged,
    saveClientFlags,
  } = useSettingsClientFlags();

  const [laborSettings, setLaborSettings] = useState(laborSettingsFormatter(laborCostSettings));
  const [clientSettings, setClientSettings] = useState({});
  const [timeOffTypes, setTimeOffTypes] = useState(() => sortTimeOfTypes(rawTimeOffTypes));

  const [laborSettingsChanged, setLaborSettingsChanged] = useState(false);
  const [clientSettingChanged, setClientSettingChanged] = useState(false);
  const [timeOffTypesChanged, setTimeOffTypesChanged] = useState(false);
  const [timeBlocksChanged, setTimeBlocksChanged] = useState(false);

  const { loadDayPartsError, dayParts, setInitialDayParts, setDayParts } = useDayParts();

  const { isSettingsValid, clearErrors } = useSettingsErrorsContext();
  const api = useSlingApi();
  const { currentUser, isOrgFeatureEnabled } = useAuth();
  const config = getAllSectionsConfig(userOrgSettings, currentUser, isOrgFeatureEnabled);
  const anchorMenuConfig = config[activeTab.value];
  const { showErrorToast } = useToast();
  const isSettingsChanged = (): boolean => {
    return (
      isUserOrgSettingsChanged ||
      isUserSettingsChanged ||
      clientSettingChanged ||
      timeOffTypesChanged ||
      laborSettingsChanged ||
      timeBlocksChanged ||
      isClientFlagsChanged
    );
  };
  const toastIntegration = useMemo(
    () => integrations?.find((el) => el.type === PosIntegrations.toast) || null,
    [integrations]
  );

  const onSaveHandle = async (): Promise<void> => {
    try {
      const promises: PromisesConfigType = {};
      const promisesArray = [];

      if (isUserSettingsChanged) {
        const settings = preSaveUserSettingsHandler({
          userSettings,
          clientSettings,
          alarmsModel,
          saveClientFlags,
        });

        promisesArray.push(api.auth.updateSettings(settings));
        promises.userSettings = {
          promiseIndex: promisesArray.length - 1,
          onSuccess: setIsUserSettingsChanged,
        };
      }
      if (isUserOrgSettingsChanged) {
        const settings = preSaveUserOrgSettingsHandler(userOrgSettings);

        promisesArray.push(api.auth.setUserOrgSettings(settings));
        promises.userOrgSettings = {
          promiseIndex: promisesArray.length - 1,
          onSuccess: setIsUserOrgSettingsChanged,
        };
      }
      if (laborSettingsChanged) {
        promisesArray.push(
          await saveLaborCostSettings({
            settings: laborSettingsParser(laborSettings),
          })
        );
        promises[PromiseType.labor] = {
          promiseIndex: promisesArray.length - 1,
          onSuccess: setLaborSettingsChanged,
        };
      }
      if (clientSettingChanged) {
        setInitialClientSettingsState(clientSettings);
        setClientSettingChanged(false);
      }
      if (timeOffTypesChanged) {
        promisesArray.push(api.timeOff.saveTimeOffTypes(timeOffTypes));
        promises['clientFlags'] = {
          promiseIndex: promisesArray.length - 1,
          onSuccess: setTimeOffTypesChanged,
        };
      }
      if (isClientFlagsChanged) {
        promisesArray.push(saveAllClientFlags());
        promises[PromiseType.clientFlags] = {
          promiseIndex: promisesArray.length - 1,
          onSuccess: setIsClientFlagsChanged,
        };
      }
      if (timeBlocksChanged) {
        promisesArray.push(api.dayParts.bulkEdit(dayParts));
        promises[PromiseType.timeBlocks] = {
          promiseIndex: promisesArray.length - 1,
          onSuccess: (): void => {
            setInitialDayParts(dayParts);
            setTimeBlocksChanged(false);
          },
        };
      }

      if (promisesArray.length) {
        const responses = await Promise.all(promisesArray);
        Object.keys(promises).forEach((key) => {
          promises[key].onSuccess(false);
        });
        if (promises[PromiseType.userSettings]) {
          const { data } = responses[promises.userSettings.promiseIndex];
          setInitialUserSettings(data);
          $rootScope.$emit('sdk:userSettings:save', data);
        }

        if (promises[PromiseType.userOrgSettings]) {
          const { data } = responses[promises.userOrgSettings.promiseIndex];
          const normalizedSettings = normalizeOrgSettings(data, personaCollection.personasDict);
          setInitialUserOrgSettings(normalizedSettings);
          $rootScope.$emit('sdk:userOrgSettings:save', normalizedSettings);
        }

        if (promises[PromiseType.timeOffTypes]) {
          const { data } = responses[promises.timeOffTypes.promiseIndex];
          $rootScope.$emit('sdk:timeOffs:save', data);
        }
      }
    } catch (error) {
      const [errorMsg] = getErrorMessages(error);
      showErrorToast(errorMsg);
    }
  };

  const onCancelHandle = (): void => {
    if (isUserSettingsChanged) {
      revertUserSettingsChanges();
      setIsUserSettingsChanged(false);
    }
    if (isUserOrgSettingsChanged) {
      revertOrgSettingsChanges();
      setIsUserOrgSettingsChanged(false);
    }
    if (clientSettingChanged) {
      setClientSettingChanged(false);
      setClientSettings(initialClientSettingsState);
    }
    if (timeOffTypesChanged) {
      setTimeOffTypesChanged(false);
      setTimeOffTypes(initialTimeOffTypes);
    }
    if (laborSettingsChanged) {
      setLaborSettings(initialLaborCostSettings.current);
      setLaborSettingsChanged(false);
    }
    if (timeBlocksChanged) {
      setTimeBlocksChanged(false);
      // @TODO: restore initials
    }
    if (isClientFlagsChanged) {
      revertClientFlagsChanges();
    }

    clearErrors();
  };

  useEffect(() => {
    if (laborCostDataLoaded) {
      initialLaborCostSettings.current = laborSettingsFormatter(laborCostSettings);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [laborCostDataLoaded]);

  useEffect(() => {
    setInitialTimeOffTypes(sortTimeOfTypes(rawTimeOffTypes));
  }, [rawTimeOffTypes]);

  useEffect(() => {
    const formattedLaborSettings = laborSettingsFormatter(laborCostSettings);

    setLaborSettings(formattedLaborSettings);
    initialLaborCostSettings.current = formattedLaborSettings;
  }, [laborCostSettings]);

  useEffect(() => {
    setLaborSettingsChanged(
      JSON.stringify(initialLaborCostSettings.current) !== JSON.stringify(laborSettings)
    );
  }, [laborSettings]);

  useEffect(() => {
    initNotificationsClientSettings();
    setInitialClientSettingsState(getClientSettings());
    setClientSettings(getClientSettings());
  }, []);

  useEffect(() => {
    setClientSettingChanged(
      JSON.stringify(initialClientSettingsState) !== JSON.stringify(clientSettings)
    );
    updateClientSetings(clientSettings);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientSettings]);

  useEffect(() => {
    setTimeOffTypesChanged(JSON.stringify(initialTimeOffTypes) !== JSON.stringify(timeOffTypes));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timeOffTypes]);

  const onTabChange = (value: number): void => {
    changeTab({ tab: tabs[value] });
  };

  const onLaborCostSettingsChange = (updatedSettingsProperties): void => {
    const updatedLaborCostSettings = {
      ...laborSettings,
      ...updatedSettingsProperties,
    };

    setLaborSettings(updatedLaborCostSettings);
  };

  const onClientSetingsChange = (updatedClientSetting): void => {
    const updatedClientSettings = {
      ...clientSettings,
      ...updatedClientSetting,
    };

    setClientSettings(updatedClientSettings);
  };

  const {
    alarmsModel,
    addAnotherAlarm,
    setAlarmsModel,
    canAddAnotherAlarm,
    removeAlarm,
    onChangeAlarmUnit,
    onChangeAlarmValue,
  } = useShiftAlarms();

  const renderTabContent = (tab: TabType): JSX.Element | null => {
    switch (tab) {
      case TabType.preferences:
        return (
          <PreferencesScreen
            personaCollection={personaCollection}
            saveClientFlags={saveClientFlags}
            googleCalendarUtils={googleCalendarUtils}
            sectionsConfig={config.preferences}
            $rootScope={$rootScope}
            isWhitelisted={isWhitelisted}
          />
        );
      case TabType.notifications:
        return (
          <NotificationsScreen
            alarmsModel={alarmsModel}
            addAnotherAlarm={addAnotherAlarm}
            setAlarmsModel={setAlarmsModel}
            canAddAnotherAlarm={canAddAnotherAlarm}
            removeAlarm={removeAlarm}
            onChangeAlarmUnit={onChangeAlarmUnit}
            onChangeAlarmValue={onChangeAlarmValue}
            setClientSettings={onClientSetingsChange}
            clientSettings={clientSettings}
            sectionsConfig={config.notifications}
          />
        );
      case TabType.companySettings:
        return (
          <CompanySettingsScreen
            orgHasOnlyOneLocation={personaCollection.locations.length === 1}
            sectionsConfig={config.companySettings}
          />
        );
      case TabType.schedule:
        return (
          <ScheduleScreen
            orgLocationsCount={personaCollection.locations.length}
            sectionsConfig={config.schedule}
          />
        );
      case TabType.timeClock: {
        return (
          <TimeclockScreen
            personasDict={personaCollection.personasDict}
            sectionsConfig={config.timeClock}
            $state={$state}
          />
        );
      }
      case TabType.laborCost: {
        if (laborSettings && personaCollection.personas) {
          return (
            <LaborCostScreen
              laborSettings={laborSettings}
              onLaborCostSettingsChange={onLaborCostSettingsChange}
              sectionsConfig={config.laborCost}
              users={personaCollection.users}
              clientFlags={clientFlags}
              saveClientFlags={saveClientFlags}
            />
          );
        }
        return null;
      }

      case TabType.timeOff:
        return (
          <TimeOffScreen
            sectionsConfig={config.timeOff}
            timeOffTypes={timeOffTypes}
            onTimeOffTypesChange={setTimeOffTypes}
          />
        );

      case TabType.integrations:
        return (
          <IntegrationsScreen
            ConnectCodeModal={ConnectCodeModal}
            IntegrationParametersModal={IntegrationParametersModal}
          />
        );

      case TabType.timeBlocks:
        return (
          <TimeBlocksScreen
            addEditDayPart={addEditDayPart}
            addEditTimeBlock={addEditTimeBlock}
            setTimeBlocksChanged={setTimeBlocksChanged}
            loadDayPartsError={loadDayPartsError}
            dayParts={dayParts}
            setDayParts={setDayParts}
          />
        );

      default:
        return null;
    }
  };

  if (!user.org.isPremium && activeTab.value === TabType.timeClock) {
    return (
      <Wrapper>
        <SettingsContainer ref={navigationWidth}>
          <SettingsNavigation
            tabs={tabs}
            initialTab={activeTab.value}
            onTabChange={onTabChange}
            maxContentWidth={navigationWidth.current?.offsetWidth}
          />
          <SettingsContent>
            <TimeClockPremiumScreen />
          </SettingsContent>
        </SettingsContainer>
      </Wrapper>
    );
  }

  if (!user.org.isPremium && activeTab.value === TabType.laborCost) {
    return (
      <Wrapper>
        <SettingsContainer ref={navigationWidth}>
          <SettingsNavigation
            tabs={tabs}
            initialTab={activeTab.value}
            onTabChange={onTabChange}
            maxContentWidth={navigationWidth.current?.offsetWidth}
          />
          <SettingsContent>
            <LaborCostPremiumScreen />
          </SettingsContent>
        </SettingsContainer>
      </Wrapper>
    );
  }

  return (
    <Wrapper>
      <SettingsContainer ref={navigationWidth}>
        <SettingsNavigation
          tabs={tabs}
          initialTab={activeTab.value}
          onTabChange={onTabChange}
          maxContentWidth={navigationWidth.current?.offsetWidth}
        />
        <SettingsTips
          activeTab={activeTab.value}
          clientFlags={clientFlags}
          saveClientFlags={saveClientFlags}
          currentUser={currentUser}
        />
        {activeTab.value === TabType.integrations && toastIntegration && (
          <ToastTips toastIntegration={toastIntegration} />
        )}
        <SettingsContent>
          <SettingsGutterColumn>
            {anchorMenuConfig && <AnchorMenu config={anchorMenuConfig} offsetTop={140} />}
            {!anchorMenuConfig && <span>&nbsp;</span>}
          </SettingsGutterColumn>
          <SectionsContainer>{renderTabContent(activeTab.value)}</SectionsContainer>
          <SettingsGutterColumn>&nbsp;</SettingsGutterColumn>
        </SettingsContent>
        {isSettingsChanged() && (
          <FooterSaveActions
            onSaveClick={(): Promise<void> => onSaveHandle()}
            onCancelClick={(): void => onCancelHandle()}
            isValid={isSettingsValid()}
          />
        )}
      </SettingsContainer>
      <ToastComponent />
    </Wrapper>
  );
};

export default SettingsScreen;
